import React, { useContext, useEffect, useState } from "react";
import { Link } from "@reach/router";
import { useApolloClient, useQuery } from "@apollo/react-hooks";
import { Plus, Search, Users, AlertCircle } from "react-feather";
import moment from "moment";
import Header from "../../../components/Header";
import { AuthContext } from "../../../services/user/AuthProvider";
import { GET_FRANCHISE, GET_FRANCHISE_ATTENTION_SCHOOLS } from "../services/graphql";
import styles from "../Franchises.module.scss";

const FranchisePortal = ({ franchiseId }) => {
  const { currentUser } = useContext(AuthContext);
  const client = useApolloClient();
  const [attention, setAttention] = useState(null);
  const [attentionError, setAttentionError] = useState(false);
  const { data, loading, error } = useQuery(GET_FRANCHISE, {
    variables: { franchiseId },
    fetchPolicy: "no-cache"
  });
  const franchise = data && data.franchise;
  const recentSchools = (franchise && franchise.recentSchools) || [];

  useEffect(() => {
    let cancelled = false;
    setAttention(null);
    setAttentionError(false);
    const loadSchools = async (page, previousSchools) => {
      const { data: result } = await client.query({
        query: GET_FRANCHISE_ATTENTION_SCHOOLS,
        variables: { franchiseId: decodeURIComponent(franchiseId), page },
        fetchPolicy: "no-cache"
      });
      if (cancelled) return;
      const directory = result && result.franchiseSchools;
      if (!directory) throw new Error("Unable to load schools");
      const schools = previousSchools.concat(directory.schools);
      if (page < directory.totalPages) return loadSchools(page + 1, schools);
      setAttention({
        total: schools.length,
        missingAdmin: schools.filter(school => !school.primaryAdministrator).length,
        missingLicences: schools.filter(school => school.annualPupilLicences <= 0).length
      });
    };
    loadSchools(1, []).catch(() => { if (!cancelled) setAttentionError(true); });
    return () => { cancelled = true; };
  }, [client, franchiseId]);

  const attentionDetails = [];
  if (attention && attention.missingAdmin > 0) {
    attentionDetails.push(`${attention.missingAdmin} ${attention.missingAdmin === 1 ? "is" : "are"} missing an administrator`);
  }
  if (attention && attention.missingLicences > 0) {
    attentionDetails.push(`${attention.missingLicences} ${attention.missingLicences === 1 ? "has" : "have"} no pupil licences`);
  }

  return (
    <>
      <Header user={currentUser} portalFranchise={franchise} portalFranchiseId={franchiseId} />
      <div className={`${styles.franchises} ${styles.portal}`}>
        {loading && <p role="status">Loading franchise...</p>}
        {error && <p role="alert">Unable to load franchise. Please try again later.</p>}
        {!loading && !error && !franchise && <p>Franchise not found.</p>}
        {!loading && !error && franchise && <>
          <div className={`${styles.header} ${styles.detailHeader}`}>
            <div>
              <p className={styles.territoryLabel}>Franchise · {franchise.name}</p>
              <h1 className="title">{franchise.name} Franchise Dashboard</h1>
              <p>Manage schools, administrators and pupil licences for the {franchise.name} territory.</p>
            </div>
            <Link to={`/franchise-portal/${franchiseId}/schools/new`} className={styles.primaryButton}>Add school</Link>
          </div>

          <div className={styles.section}>
            <h2>Overview</h2>
            <div className={styles.overview}>
              <div className={styles.overviewCard}>
                <h3>Schools</h3>
                <strong>{(franchise.totalSchoolsCount || 0).toLocaleString("en-GB")}</strong>
              </div>
              <div className={styles.overviewCard}>
                <h3>Pupil licences</h3>
                <strong>{(franchise.totalPupilLicences || 0).toLocaleString("en-GB")}</strong>
              </div>
              <div className={styles.overviewCard}>
                <h3>Teachers</h3>
                <strong>{(franchise.totalTeachers || 0).toLocaleString("en-GB")}</strong>
              </div>
              <div className={styles.overviewCard}>
                <h3>Parents</h3>
                <strong>{(franchise.totalParents || 0).toLocaleString("en-GB")}</strong>
              </div>
            </div>
          </div>

          <div className={styles.section}>
            <h2>Tools</h2>
            <div className={styles.portalTools}>
              <div className={`${styles.overviewCard} ${styles.portalTool}`}>
                <span className={`${styles.portalToolIcon} ${styles.portalAddIcon}`}><Plus size={30} aria-hidden="true" /></span>
                <div><h3>Add a school</h3><p>Create the school, first admin and licence period.</p></div>
              </div>
              <div className={`${styles.overviewCard} ${styles.portalTool}`}>
                <span className={styles.portalToolIcon}><Search size={22} aria-hidden="true" /></span>
                <div><h3>Manage schools</h3><p>Search, open and update existing schools.</p></div>
              </div>
              <div className={`${styles.overviewCard} ${styles.portalTool}`}>
                <span className={styles.portalToolIcon}><Users size={22} aria-hidden="true" /></span>
                <div><h3>Admin users</h3><p>Invite or remove {franchise.name} franchise administrators.</p></div>
              </div>
            </div>
          </div>

          <div className={styles.section}>
            <h2>Schools requiring attention</h2>
            <div className={`${styles.addTerritory} ${styles.portalAttention}`}>
              <span className={`${styles.addIcon} ${styles.portalAlertIcon}`}><AlertCircle size={24} aria-hidden="true" /></span>
              <div className={styles.addDetails}>
                <h3>{attentionError ? "Unable to load schools requiring attention." : attention
                  ? `${attention.total} ${attention.total === 1 ? "school has" : "schools have"} incomplete setup`
                  : "Loading schools requiring attention..."}</h3>
                {attentionDetails.length > 0 && <p>{attentionDetails.join(" · ")}</p>}
              </div>
              <Link to={`/franchise-portal/${franchiseId}/schools`} className={styles.primaryButton}>Review</Link>
            </div>
          </div>

          <div className={styles.section}>
            <div className={styles.directoryHeading}>
              <h2>Recently added schools</h2>
              <button type="button" className={styles.textButton}>View all</button>
            </div>
            {recentSchools.length === 0 ? <p>No recently added schools.</p> : (
              <div className={recentSchools.length > 3 ? `${styles.schoolsTableWrapper} ${styles.schoolsScroll}` : styles.schoolsTableWrapper}>
                <table className={styles.schoolsTable}>
                  <thead>
                    <tr>
                      <th scope="col">School</th>
                      <th scope="col">Pupils</th>
                      <th scope="col">Start date</th>
                      <th scope="col">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentSchools.map(school => (
                      <tr key={school.id}>
                        <th scope="row"><Link to={`/franchise-portal/${franchiseId}/schools/${school.id}`}>{school.name}</Link></th>
                        <td>{(school.pupilsCount || 0).toLocaleString("en-GB")}</td>
                        <td>{school.startDate ? moment(school.startDate).format("D MMM YYYY") : "—"}</td>
                        <td><span className={styles.detailStatus} data-status={(school.onboardingStatusLabel || "").toLowerCase()}>{school.onboardingStatusLabel}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>}
      </div>
    </>
  );
};

export default FranchisePortal;
